import { Checkbox, Drawer, FormControl, FormControlLabel, Grid2, InputLabel, MenuItem, Select, Tab, FormHelperText } from "@mui/material";
import { useEffect, useState } from "react";
import { SelectValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { RoleEnum, RoleStringEnum } from "../../_common/enum";
import { StandardButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { StandardTextValidator } from "../../_components/textValidator/standardTextValidator";
import { toastr } from "../../_components/toastr/toastr";
import { RESET_PASSWORD_ERROR_MESSAGES, RESET_PASSWORD_HELPER_TEXT, RESET_PASSWORD_VALIDATORS } from "../../_const/security.const";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { accountService } from "../../_services/accounts.service";
import { usersService } from "../../_services/users.service";
import { getDateString, getToday } from "../../_helpers/dateTime.helper";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";
import { DataTypeAutoComplete } from "../../_components/autocomplete/dataType.autocomplete";

export const UsersManageDrawer = (params: { isOpen?: boolean, onSave(): void, onClose(): void }) => {

    const resetData = () => ({
        id: "",
        name: "",
        username: "",
        email: "",
        phoneNumber: "",
        role: "",
        roles: [],
        password: "",
        confirmPassword: "",
        startDate: "",
        endDate: ""
    });

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [passwordChange, setPasswordChange] = useState(false);

    useEffect(() => {
        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    useEffect(() => {
        if (isOpen == false)
            setIsOpen(true);
    }, []);

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return usersService
            .get(id)
            .then(
                (json) => {
                    setData((_data: any) => ({
                        ...json,
                        username: json.username ?? json.Username ?? "",
                        email: json.email ?? json.Email ?? "",
                        role: json.roles,
                        startDate: getDateString(json.startDate),
                        endDate: getDateString(json.endDate)
                    }));
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();

        setIsSaving(true);

        var _data = { ...data };
        _data.roles = _data.id != null ? [] : _data.roles || [];
        _data.roles.push(_data.role);

        return accountService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        setIsLoading(true);
        return accountService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={isOpen}
            ModalProps={{
                keepMounted: true
            }}>

            <DrawerHeader name={(!!id) ? "Update User" : "Create User"} onClose={params.onClose} />

            <Grid2 size={{ xs: 12 }}>
                <ValidatorForm
                    onSubmit={submit}
                    noValidate>

                    <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="name"
                                name="name"
                                label="Name"
                                required
                                fullWidth
                                validators={['required', 'minStringLength:1']}
                                errorMessages={['Required field', "Field cannot be empty"]}
                                value={data.name}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="username"
                                name="username"
                                label="Username"
                                required
                                fullWidth
                                validators={['required', 'minStringLength:1']}
                                errorMessages={['Required field', "Field cannot be empty"]}
                                value={data.username}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="email"
                                name="email"
                                label="Email (optional)"
                                fullWidth
                                validators={data.email?.trim() ? ['isEmail'] : []}
                                errorMessages={data.email?.trim() ? ['Invalid email'] : []}
                                value={data.email}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="phoneNumber"
                                name="phoneNumber"
                                label="Phone number"
                                fullWidth
                                required
                                validators={["required"]}
                                errorMessages={"Field cannot be empty"}
                                value={data.phoneNumber}
                                onChange={handleChange}
                            />
                        </Grid2>

                        {(!data.id) &&
                            <Grid2 size={{ xs: 12 }}>
                                <StandardTextValidator
                                    autoComplete="new-password"
                                    required
                                    fullWidth
                                    label="Initial Password"
                                    name="password"
                                    value={data.password}
                                    disabled={isLoading}
                                    onChange={handleChange}
                                    type="password"
                                    validators={RESET_PASSWORD_VALIDATORS(true)}
                                    errorMessages={RESET_PASSWORD_ERROR_MESSAGES(true)}
                                    helperText={!data.password ? RESET_PASSWORD_HELPER_TEXT : "Password is valid"}
                                />
                            </Grid2>
                        }

                        {(!!data.id) &&
                            <Grid2 size={{ xs: 12 }}>
                                <FormControlLabel control={<Checkbox checked={passwordChange} onChange={() => setPasswordChange(!passwordChange)} />} label="Change Password" />

                                {passwordChange === true &&
                                    <StandardTextValidator
                                        autoComplete="off"
                                        fullWidth
                                        label="Password"
                                        name="password"
                                        value={data?.password}
                                        onChange={handleChange}
                                        type="password"
                                        validators={RESET_PASSWORD_VALIDATORS(true)}
                                        errorMessages={RESET_PASSWORD_ERROR_MESSAGES(true)}
                                        helperText={!data?.password ? "Only Update if password change is required. " + RESET_PASSWORD_HELPER_TEXT : "✓ Password is valid"} />
                                }
                            </Grid2>
                        }

                        <Grid2 size={{ xs: 12 }}>
                            <SelectValidator
                                label="User Role"
                                name="role"
                                value={data.role}
                                onChange={handleChange}
                                required
                                fullWidth
                                variant="outlined"
                                validators={["required"]}
                                errorMessages={["User Role is required"]}
                            >
                                {Object.keys(RoleStringEnum).map((role) => {
                                    const objectValue = parseInt(role);

                                    if (!isNaN(objectValue)) {
                                        return null;
                                    }

                                    return (
                                        <MenuItem
                                            key={role}
                                            value={RoleStringEnum[role as keyof typeof RoleEnum]}
                                        >
                                    {role === "SocialCareLeader" ? "Pre-approved Access" : role}
                                        </MenuItem>
                                    );
                                })}
                            </SelectValidator>
                        </Grid2>

                        {data.role == RoleStringEnum.TemporaryUser && <>
                            <Grid2 size={{ xs: 12 }}>
                                <StandardTextValidator
                                    key="startDate"
                                    name="startDate"
                                    label="Start Date"
                                    required
                                    inputProps={{ min: getDateString(getToday()) }}
                                    type="date"
                                    fullWidth
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    value={data.startDate}
                                    onChange={handleChange}
                                />
                            </Grid2>
                        </>
                        }
                        {data.role == RoleStringEnum.TemporaryUser && <>
                            <Grid2 size={{ xs: 12 }}>
                                <StandardTextValidator
                                    key="endDate"
                                    name="endDate"
                                    label="End Date"
                                    required
                                    type="date"
                                    fullWidth
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    value={data.endDate}
                                    onChange={handleChange}
                                />
                            </Grid2>
                        </>
                        }
                        {data.role == RoleStringEnum.TemporaryUser && <>
                            <Grid2 size={{ xs: 12 }}>
                                <DataTypeAutoComplete
                                    name="centreId"
                                    label="Centre"
                                    required
                                    value={data.centreId}
                                    onChange={handleChange} dataType={DataTypeEnum.Centres} />
                            </Grid2>
                        </>
                        }

                        <Grid2 size={{ xs: 12 }}>
                            <Grid2 container spacing={1}>
                                <Grid2 size={{ xs: !data.id || data?.isActive === false ? 12 : 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="save"
                                        type="submit">
                                        {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                    </StandardButton>
                                </Grid2>
                                {(!!id && data?.isActive === true) &&
                                    <Grid2 size={{ xs: 6 }}>
                                        <StandardButton
                                            saving={saving}
                                            loading={isLoading}
                                            icon="block"
                                            variant="warning"
                                            onClick={disable}>
                                            Disable
                                        </StandardButton>
                                    </Grid2>
                                }
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </ValidatorForm>
            </Grid2>
        </Drawer>
    );
};